import { findPhoneNumbersInText } from "libphonenumber-js";

/* ---------------------------------------------------------------------------
 * Bare contacts -> mailto:/tel: links, before react-markdown renders.
 *
 * `CustomLinkRenderer` turns a link into an email/phone chip by looking at its
 * href, so it only ever saw contacts the model happened to write as a markdown
 * link. The prompt only asks for that on URLs ("ALWAYS format any URL […] as a
 * markdown link"), so a reply like
 *
 *   - **Reception 24h:** +39 0464 554433
 *   - **Prenotazioni:** info@grandhotelliberty.it
 *
 * produced no `a` node at all, and therefore no chips. remark-gfm's autolink
 * literals would have covered the email and nothing else — phone numbers are
 * not part of GFM — so this does both, deterministically, instead of depending
 * on the model getting the syntax right turn after turn.
 *
 * Phone detection is `libphonenumber-js` (already a dependency, same library
 * the handoff form validates with), NOT a regex: it checks the run against the
 * real numbering plan, so prices (`1.500,00`), quote ids (`2026000123`) and
 * room numbers are rejected, and a domestic number comes back as E.164 —
 * `0464 554433` dials as `tel:+390464554433`, so it works from a guest's phone
 * abroad too.
 *
 * The numbering plan alone is NOT enough, though: `.`, `-` and spaces are all
 * phone separators to libphonenumber, so a date or an opening-hours range whose
 * digits happen to fit a plan comes back "valid" —
 *
 *   06.08.2026     -> +3906082026   (Rome, 06 + 8 digits)
 *   08.00 - 22.00  -> +3908002200
 *
 * and a guest reading "da giovedì 06.08.2026" got a tap-to-call chip on the
 * date. Only `/` saved us: libphonenumber does not treat it as a separator,
 * which is why `12/08/2026` was rejected and the bug hid behind it. So a run
 * that is shaped like a date or a clock range is dropped before it can become
 * a chip — see `readsAsDateOrTime`.
 *
 * Runs on bot copy only. Guest copy keeps remark-breaks alone: what the guest
 * typed is not a control surface.
 * ------------------------------------------------------------------------- */

/**
 * Tenants are Italian hotels, and a domestic number needs a region to be
 * validated at all. A number written with `+…` carries its own country and
 * ignores this.
 */
const DEFAULT_REGION = "IT";

/** Containers whose text is already a target, or is not prose at all. */
const OPAQUE_NODES = new Set([
  "link",
  "linkReference",
  "definition",
  "image",
  "imageReference",
  "code",
  "inlineCode",
  "html",
]);

/**
 * Local part per RFC 5322's practical subset, domain as a labelled hostname
 * with at least one dot — `info@grandhotelliberty.it`, never a bare `@handle`.
 */
const EMAIL =
  /[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)+/g;

/**
 * Glued to any of these on the left, the run belongs to something else — most
 * of all `/`, which is how a bare `wa.me/390464554433` would otherwise have its
 * digits torn out into a `tel:` link.
 */
const BOUND_LEFT = /[\w/@.+-]/;

/** Every character libphonenumber will swallow between two groups of digits. */
const SEP = "[.\\-\\u2013\\u2014/\\s]";

/** `06.08.2026`, `6-8-26`, `2026-08-06`: three groups around a middle month. */
const THREE_GROUPS = new RegExp(`^(\\d{1,4})${SEP}(\\d{1,2})${SEP}(\\d{2,4})$`);

/** `08.00 - 22.00`, `9:30-12:00`: two clock times, one dash. */
const CLOCK_RANGE = new RegExp(
  `^\\d{1,2}[.:]\\d{2}\\s*[-\\u2013\\u2014]\\s*\\d{1,2}[.:]\\d{2}$`
);

const isDay = (n: number) => n >= 1 && n <= 31;
const isMonth = (n: number) => n >= 1 && n <= 12;
/** `26` or `2026`, not `3456`: a 4-digit year a hotel writes is 19xx/20xx. */
const isYear = (group: string) => {
  const n = Number(group);
  return group.length === 2 ? n >= 0 && n <= 99 : n >= 1900 && n <= 2099;
};

/**
 * A calendar date or a clock range, not a number to dial. Applied only to runs
 * written without a `+`: a leading country code is the author saying "phone",
 * and `+39 06.08.2026` is not a thing a reply contains.
 *
 * Real numbers survive because the month group gives them away — `348 123 4567`
 * has `123` in the middle, `06 12 34567` a 5-digit tail, `0464 554433` only two
 * groups.
 */
const readsAsDateOrTime = (value: string): boolean => {
  const run = value.trim();
  if (CLOCK_RANGE.test(run)) return true;

  const groups = THREE_GROUPS.exec(run);
  if (!groups) return false;

  const [, first, month, last] = groups;
  if (!isMonth(Number(month))) return false;

  // dd-mm-yyyy as a European reply writes it, or yyyy-mm-dd from a tool result.
  return (
    (isDay(Number(first)) && isYear(last)) ||
    (isYear(first) && first.length === 4 && isDay(Number(last)))
  );
};

interface Hit {
  index: number;
  value: string;
  url: string;
}

const isGlued = (text: string, index: number, value: string): boolean => {
  const before = text[index - 1] ?? "";
  const after = text[index + value.length] ?? "";

  return BOUND_LEFT.test(before) || /[\w@/]/.test(after);
};

const emailHits = (text: string): Hit[] => {
  const hits: Hit[] = [];
  EMAIL.lastIndex = 0;

  for (let m = EMAIL.exec(text); m; m = EMAIL.exec(text)) {
    const value = m[0];
    if (!isGlued(text, m.index, value)) {
      hits.push({ index: m.index, value, url: `mailto:${value}` });
    }
  }

  return hits;
};

const phoneHits = (text: string): Hit[] =>
  findPhoneNumbersInText(text, DEFAULT_REGION)
    .map(({ startsAt, endsAt, number }) => ({
      index: startsAt,
      value: text.slice(startsAt, endsAt),
      url: `tel:${number.number}`,
    }))
    .filter((hit) => !isGlued(text, hit.index, hit.value))
    .filter(
      (hit) => hit.value.trim().startsWith("+") || !readsAsDateOrTime(hit.value)
    );

/** text node -> [text, link, text, …]; empty array when nothing matched. */
const splitTextNode = (text: string): any[] => {
  const hits = [...emailHits(text), ...phoneHits(text)].sort(
    (a, b) => a.index - b.index
  );
  if (!hits.length) return [];

  const out: any[] = [];
  let cursor = 0;

  for (const hit of hits) {
    // Overlap guard: the digits of an already-linked email must not be split
    // out a second time as a phone number.
    if (hit.index < cursor) continue;

    if (hit.index > cursor) {
      out.push({ type: "text", value: text.slice(cursor, hit.index) });
    }
    out.push({
      type: "link",
      url: hit.url,
      title: null,
      children: [{ type: "text", value: hit.value }],
    });
    cursor = hit.index + hit.value.length;
  }

  if (cursor < text.length) {
    out.push({ type: "text", value: text.slice(cursor) });
  }

  return out;
};

const walk = (node: any) => {
  const children = node?.children;
  if (!Array.isArray(children)) return;

  for (let i = 0; i < children.length; i += 1) {
    const child = children[i];
    if (!child || OPAQUE_NODES.has(child.type)) continue;

    if (child.type === "text") {
      const parts = splitTextNode(String(child.value ?? ""));
      if (parts.length) {
        children.splice(i, 1, ...parts);
        i += parts.length - 1;
      }
      continue;
    }

    walk(child);
  }
};

/** remark plugin: mutates the mdast in place, the way remark-breaks does. */
const remarkContactAutolink = () => (tree: any) => {
  walk(tree);
};

export default remarkContactAutolink;
